<template>
    <div class="product-map__map">
        <MapFilter
            v-if="categories.length > 1"
            ref="filter"
            :categories="categories"
            @change="filterByCategory"
        />
        <div ref="map" class="product-map__map"></div>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { uniqWith, flatten, isEqual, get } from 'lodash';
import MapFilter from './ProductMapFilter.vue';

export default {
    components: {
        MapFilter
    },

    props: {
        mapstyle: {
            type: String,
            required: true
        },

        longitude: {
            type: Number,
            required: true
        },

        latitude: {
            type: Number,
            required: true
        },

        maxzoom: {
            type: Number,
            required: true
        },

        minzoom: {
            type: Number,
            required: true
        },

        zoom: {
            type: Number,
            required: true
        },

        products: {
            type: Array,
            required: true
        },

        categories: {
            type: Array,
            required: true
        },

        settings: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            map: null,
            activeMarkerId: null,
            activePopupId: null
        };
    },

    computed: {
        // A list of all categories used across the products.
        // Used to create marker layers and set icons.
        allCategories() {
            const all = flatten(this.products.map((product) => product.categories));
            return uniqWith(all, isEqual);
        },

        categoryLayers() {
            return this.allCategories.map((category) => `markers-${category.slug}`);
        },

        markerWatcher() {
            return `${this.activeMarkerId}|${this.activePopupId}`;
        },

        featureCollection() {
            return {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: this.products.map((product) => ({
                        id: product.id,
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [product.marker.longitude, product.marker.latitude]
                        },
                        properties: product
                    }))
                },
                ...this.settings.map
            };
        }
    },

    watch: {
        markerWatcher() {
            const activeMarkerId = this.activeMarkerId || 'none';
            const activePopupId = this.activePopupId || 'none';

            const filters =
                activeMarkerId === activePopupId ? activeMarkerId : [activeMarkerId, activePopupId];

            this.allCategories.forEach((category) => {
                this.map.setLayoutProperty(`markers-${category.slug}`, 'icon-image', [
                    'match',
                    ['get', 'id'],
                    filters,
                    `${category.slug}-active`,
                    `${category.slug}-inactive`
                ]);
            });
        }
    },

    mounted() {
        mapboxgl.accessToken =
            'pk.eyJ1IjoidmlzaXRib3Vya2UiLCJhIjoiY211emprMHoyMDlkdDJ5b2g0NjZybDJxbyJ9.qCGqCFl-r1-5EeXQ1cJmJA';

        this.map = new mapboxgl.Map({
            container: this.$refs.map,
            style: `mapbox://styles/${this.mapstyle}`,
            center: [this.longitude, this.latitude],
            zoom: this.zoom,
            minZoom: this.minzoom,
            maxZoom: this.maxzoom
        });

        // General map config
        const navControl = new mapboxgl.NavigationControl({ visualizePitch: true });
        this.map.addControl(navControl, 'bottom-right');
        this.map.scrollZoom.disable();

        this.map.on('load', () => {
            this.map.addSource('products', this.featureCollection);

            this.initMarkers();
            this.initPopups();
            this.initClusters();
        });

        this.map.on('click', () => {
            this.$refs.filter.close();
        });
    },

    methods: {
        loadIcon({ handle, icon, state }) {
            return new Promise((resolve) => {
                this.map.loadImage(icon, (error, image) => {
                    if (error) {
                        this.map.loadImage(
                            `/themes/bourkeshire/images/mapbox/pins/pin-default-${state}.png`,
                            (error, fallback) => {
                                this.map.addImage(handle, fallback);
                                resolve();
                            }
                        );
                    } else {
                        this.map.addImage(handle, image);
                        resolve();
                    }
                });
            });
        },

        initMarkers() {
            const iconPromises = this.allCategories.reduce((acc, category) => {
                acc.push(
                    this.loadIcon({
                        handle: `${category.slug}-active`,
                        icon: category.active,
                        state: 'active'
                    })
                );
                acc.push(
                    this.loadIcon({
                        handle: `${category.slug}-inactive`,
                        icon: category.inactive,
                        state: 'inactive'
                    })
                );
                return acc;
            }, []);

            Promise.all(iconPromises).then(() => {
                this.addMarkerLayers();
            });
        },

        addMarkerLayers() {
            this.allCategories.forEach((category) => {
                this.map.addLayer(
                    {
                        id: `markers-${category.slug}`,
                        type: 'symbol',
                        source: 'products',
                        filter: ['==', 'primaryCategory', category.slug],
                        layout: {
                            'icon-allow-overlap': true,
                            'icon-image': `${category.slug}-inactive`,
                            ...this.settings.marker
                        }
                    },
                    'settlement-subdivision-label'
                );

                this.map.on('mouseenter', `markers-${category.slug}`, () => {
                    this.map.getCanvas().style.cursor = 'pointer';
                });

                this.map.on('mouseleave', `markers-${category.slug}`, () => {
                    this.map.getCanvas().style.cursor = '';
                });
            });

            this.map.on('mousemove', (e) => {
                const markers = this.map.queryRenderedFeatures(e.point, {
                    layers: this.categoryLayers
                });

                const activeLayer = get(markers, '[0].layer.id');
                this.activeMarkerId = get(markers, '[0].properties.id');
                if (activeLayer) this.map.moveLayer(activeLayer, 'settlement-subdivision-label');
            });
        },

        initClusters() {
            this.map.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'products',
                filter: ['has', 'point_count'],
                ...this.settings.cluster
            });

            this.map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'products',
                filter: ['has', 'point_count'],
                ...this.settings.clusterCount
            });

            this.map.on('click', 'clusters', (e) => {
                const features = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
                const clusterId = features[0].properties.cluster_id;

                this.map.getSource('products').getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err) return;

                    this.map.easeTo({
                        center: features[0].geometry.coordinates,
                        zoom: zoom + 2
                    });
                });
            });

            this.map.on('mouseenter', 'clusters', () => {
                this.map.getCanvas().style.cursor = 'pointer';
            });

            this.map.on('mouseleave', 'clusters', () => {
                this.map.getCanvas().style.cursor = '';
            });
        },

        initPopups() {
            const template = (product) =>
                `<div class="product-map__overlay">
                    <div class="product-map__image-holder">
                        <a class="product-map__content-link" target="_blank" href="${product.properties.link}">
                            <img class="product-map__content-image" src="${product.properties.image}" width="360" height="220"/>
                        </a>
                    </div>

                    <div class="product-map__content-holder">
                        <a class="product-map__content-link" href="${product.properties.link}">
                            <h2 class="product-map__content-title">${product.properties.title}</h2>
                        </a>

                        <p class="product-map__content-description">${product.properties.description}</p>
                        <a class="product-map__content-link product-map__button btn btn--yellow" href="${product.properties.link}">LEARN MORE</a>
                    </div>

                    <div class="product-map__mobile-toggle">
                        <button class="product-map__overlay-toggle" />
                    </div>
                </div>`;

            const popup = new mapboxgl.Popup({ className: 'product-map__overlay-container' });
            let savedZoom = 0;

            popup.on('close', () => {
                if (!this.activePopupId) return;

                this.activePopupId = null;

                this.map.flyTo({
                    pitch: 0,
                    bearing: 0,
                    zoom: savedZoom
                });
            });

            popup.on('open', (e) => {
                const container = e.target._container;
                const toggle = container.querySelector('.product-map__overlay-toggle');

                toggle.onclick = () => {
                    container.classList.add('is-expanded');
                };
            });

            this.map.on('click', (e) => {
                const markers = this.map.queryRenderedFeatures(e.point, {
                    layers: this.categoryLayers
                });

                if (!markers.length) return;

                const product = markers[0];
                const marker = JSON.parse(product.properties.marker);
                savedZoom = this.map.getZoom();
                this.activePopupId = product.properties.id;

                const flyToSettings = {
                    zoom: 0,
                    pitch: 0,
                    bearing: 0,
                    ...this.settings.flyTo
                };

                this.map.flyTo({
                    center: [marker.longitude, marker.latitude],
                    zoom: this.savedZoom > flyToSettings.zoom ? this.savedZoom : flyToSettings.zoom,
                    pitch: flyToSettings.pitch,
                    bearing: flyToSettings.bearing
                });

                popup
                    .setLngLat(product.geometry.coordinates)
                    .setHTML(template(product))
                    .addTo(this.map);
            });
        },

        filterByCategory(category) {
            const clone = JSON.parse(JSON.stringify(this.featureCollection.data));

            if (category) {
                const filteredSet = clone.features.filter(
                    (p) => p.properties.primaryCategory === category.slug
                );
                clone.features = filteredSet;
            }

            this.map.getSource('products').setData(clone);
        }
    }
};
</script>
